import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, X, Clock, Undo2, Mail, Send, Loader2, Users, ListChecks, CheckCircle2, XCircle, AlertCircle } from "lucide-react";
import { getFormationCandidacies, decideCandidacy, previewDecisionEmail, setAttendance, resendRecap, replySubmission } from "@/lib/api";

const STATUS = {
  en_attente: { label: "En attente", tone: "text-amber-300 border-amber-400/40" },
  acceptee: { label: "Acceptée", tone: "text-green-300 border-green-400/40" },
  refusee: { label: "Refusée", tone: "text-red-300 border-red-400/40" },
  liste_attente: { label: "Liste d'attente", tone: "text-sky-300 border-sky-400/40" },
  annulee: { label: "Annulée", tone: "text-white/50 border-white/15" },
};
const ATT = {
  present: { label: "Présent", tone: "text-green-300 border-green-400/40" },
  absent: { label: "Absent", tone: "text-red-300 border-red-400/40" },
  retard: { label: "En retard", tone: "text-amber-300 border-amber-400/40" },
  non_renseigne: { label: "Non renseigné", tone: "text-white/50 border-white/15" },
};

const Pill = ({ children, tone }) => (
  <span className={`inline-flex items-center text-[9px] uppercase tracking-[0.12em] rounded-full px-2 py-0.5 border bg-black/60 ${tone}`}>{children}</span>
);

// Panneau « Gestion des candidatures » d'une formation (ou d'une session).
// Réutilisé dans la fiche formation et la boîte de réception.
export const CandidaciesPanel = ({ refId, refTitle, sessions = [], defaultSession = "", onChanged }) => {
  const [sessionId, setSessionId] = useState(defaultSession);
  const [statusFilter, setStatusFilter] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null); // {action, cand}
  const [emailDraft, setEmailDraft] = useState(null); // {action, cand, subject, message, sending}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await getFormationCandidacies(refId, sessionId || undefined);
      setData(d);
    } catch { toast.error("Impossible de charger les candidatures."); }
    finally { setLoading(false); }
  }, [refId, sessionId]);
  useEffect(() => { load(); }, [load]);

  const cand = (data?.candidacies || []).filter((c) => !statusFilter || c.status === statusFilter);
  const currentSession = (data?.sessions || []).find((s) => s.id === sessionId) || null;

  const askDecision = (action, c) => setConfirm({ action, c });
  const doDecision = async () => {
    if (!confirm) return;
    const { action, c } = confirm;
    setBusy(true);
    try {
      const res = await decideCandidacy(c.id, action);
      if (res.status === "full") { toast.error(res.detail || "Session complète."); setConfirm(null); setBusy(false); return; }
      toast.success({ accepter: "Candidature acceptée — place réservée.", refuser: "Candidature refusée.", liste_attente: "Mise en liste d'attente.", annuler: "Inscription annulée — place libérée.", remettre_en_attente: "Repasse en attente." }[action] || "Décision enregistrée.");
      setConfirm(null);
      await load();
      onChanged && onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec de la décision."); }
    finally { setBusy(false); }
  };

  const openEmail = async (action, c) => {
    setBusy(true);
    try {
      const t = await previewDecisionEmail(c.id, action);
      setEmailDraft({ action, c, subject: t.subject, message: t.message, sending: false });
    } catch { toast.error("Impossible de préparer l'e-mail."); }
    finally { setBusy(false); }
  };
  const sendEmail = async () => {
    if (!emailDraft) return;
    setEmailDraft((d) => ({ ...d, sending: true }));
    try {
      const res = await replySubmission(emailDraft.c.id, emailDraft.message);
      if (res.status === "success") { toast.success("E-mail envoyé au candidat."); }
      else { toast.error("Décision conservée, mais l'e-mail n'a PAS été envoyé. Réessayez."); }
      setEmailDraft(null);
      await load();
    } catch { toast.error("Échec de l'envoi de l'e-mail."); setEmailDraft((d) => ({ ...d, sending: false })); }
  };

  const markAttendance = async (c, state) => {
    try {
      await setAttendance(c.id, state);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Impossible de marquer la présence."); }
  };

  const resendList = async () => {
    if (!sessionId) return;
    setBusy(true);
    try {
      const r = await resendRecap(refId, sessionId);
      toast[r.status === "sent" ? "success" : "error"](r.status === "sent" ? `Liste actualisée envoyée (${r.confirmed} confirmés).` : "Échec d'envoi de la liste.");
    } catch { toast.error("Échec de l'envoi."); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="candidacies-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-[11px] tracking-[0.25em] uppercase text-electric inline-flex items-center gap-2">
          <Users size={14} /> Candidatures{refTitle ? ` — ${refTitle}` : ""}
        </span>
        <div className="flex items-center gap-2">
          <select value={sessionId} onChange={(e) => setSessionId(e.target.value)} data-testid="cand-session-filter"
            className="bg-[#0e0e0e] border border-white/10 rounded px-2 py-1.5 text-white text-xs focus:border-electric outline-none">
            <option value="">Toutes les sessions</option>
            {sessions.map((s) => <option key={s.id} value={s.id}>{s.date || "Session"}{s.time ? ` · ${s.time}` : ""}</option>)}
          </select>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="cand-status-filter"
            className="bg-[#0e0e0e] border border-white/10 rounded px-2 py-1.5 text-white text-xs focus:border-electric outline-none">
            <option value="">Tous statuts</option>
            {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </div>
      </div>

      {/* Compteurs essentiels de la session sélectionnée */}
      {currentSession && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <Stat label="Capacité" value={currentSession.capacity || "—"} />
          <Stat label="Confirmées" value={currentSession.confirmed} tone="text-green-300" />
          <Stat label="En attente" value={currentSession.pending} tone="text-amber-300" />
          <Stat label="Disponibles" value={currentSession.available == null ? "—" : currentSession.available} tone="text-electric" />
        </div>
      )}
      {currentSession && (
        <div className="flex justify-end">
          <button onClick={resendList} disabled={busy} data-testid="resend-recap"
            className="inline-flex items-center gap-2 text-xs text-white/70 hover:text-electric disabled:opacity-50">
            {busy ? <Loader2 size={13} className="animate-spin" /> : <Mail size={13} />} Renvoyer la liste actualisée
          </button>
        </div>
      )}

      {loading ? (
        <div className="py-10 flex justify-center"><Loader2 className="animate-spin text-electric" /></div>
      ) : cand.length === 0 ? (
        <p className="text-secondary2 text-sm py-6 text-center">Aucune candidature{statusFilter ? " pour ce statut" : ""}.</p>
      ) : (
        <div className="space-y-2">
          {cand.map((c) => {
            const st = STATUS[c.status] || STATUS.en_attente;
            return (
              <div key={c.id} className="rounded-lg border border-white/10 bg-[#0a0a0a] p-3" data-testid={`cand-${c.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm text-white font-medium">{c.name || "Sans nom"}</span>
                      <Pill tone={st.tone}>{st.label}</Pill>
                      {(c.duplicateCount || 0) > 0 && <Pill tone="text-fuchsia-300 border-fuchsia-400/40">doublon ×{(c.duplicateCount || 0) + 1}</Pill>}
                    </div>
                    <div className="text-xs text-secondary2 mt-1">{c.email}{c.phone ? ` · ${c.phone}` : ""}</div>
                    <div className="text-[11px] text-secondary2 mt-0.5">
                      {c.refTitle}{c.sessionLabel ? ` · ${c.sessionLabel}` : ""} · reçue le {(c.created_at || "").slice(0, 16).replace("T", " à ")}
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5">
                    {c.status !== "acceptee" && (
                      <button onClick={() => askDecision("accepter", c)} disabled={busy} data-testid={`cand-accept-${c.id}`} title="Accepter (réserve une place)"
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded border border-green-400/30 text-green-300 text-[11px] hover:bg-green-400/10"><Check size={12} /> Accepter</button>
                    )}
                    {c.status !== "refusee" && c.status !== "annulee" && (
                      <button onClick={() => askDecision("refuser", c)} disabled={busy} data-testid={`cand-refuse-${c.id}`} title="Refuser"
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded border border-red-400/30 text-red-300 text-[11px] hover:bg-red-400/10"><X size={12} /> Refuser</button>
                    )}
                    {c.status !== "liste_attente" && c.status !== "annulee" && (
                      <button onClick={() => askDecision("liste_attente", c)} disabled={busy} data-testid={`cand-waitlist-${c.id}`} title="Mettre en liste d'attente"
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded border border-sky-400/30 text-sky-300 text-[11px] hover:bg-sky-400/10"><Clock size={12} /> Attente</button>
                    )}
                    {c.status === "acceptee" && (
                      <button onClick={() => askDecision("annuler", c)} disabled={busy} data-testid={`cand-cancel-${c.id}`} title="Annuler (libère la place)"
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded border border-white/20 text-white/70 text-[11px] hover:bg-white/5"><Undo2 size={12} /> Annuler</button>
                    )}
                    {c.status !== "en_attente" && c.status !== "annulee" && (
                      <button onClick={() => openEmail(c.status === "acceptee" ? "accepter" : c.status === "refusee" ? "refuser" : "liste_attente", c)} disabled={busy} data-testid={`cand-email-${c.id}`} title="E-mail de décision (pré-rempli, à valider)"
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded border border-electric/40 text-electric text-[11px] hover:bg-electric/10"><Mail size={12} /> E-mail</button>
                    )}
                  </div>
                </div>

                {c.message && <p className="text-xs text-white/70 mt-2 whitespace-pre-line border-l-2 border-white/10 pl-2">{c.message}</p>}
                {(c.extraMessages || []).map((m, i) => (
                  <p key={i} className="text-xs text-white/50 mt-1 whitespace-pre-line border-l-2 border-fuchsia-400/30 pl-2">
                    <span className="text-[10px] uppercase tracking-wider text-fuchsia-300/70">Message complémentaire · {(m.created_at || "").slice(0, 10)}</span><br />{m.message}
                  </p>
                ))}
                {(c.decisionHistory || []).length > 0 && (
                  <div className="mt-2 space-y-0.5">
                    {c.decisionHistory.map((h, i) => (
                      <div key={i} className="text-[10px] text-secondary2">
                        {(h.at || "").slice(0, 16).replace("T", " ")} · {STATUS[h.to]?.label || h.to}{h.note ? ` — ${h.note}` : ""} · par {h.by}
                      </div>
                    ))}
                  </div>
                )}

                {/* Présence (seulement si acceptée et session sélectionnée) */}
                {c.status === "acceptee" && sessionId && (
                  <div className="mt-2 pt-2 border-t border-white/10 flex flex-wrap items-center gap-1.5">
                    <ListChecks size={13} className="text-secondary2" />
                    <span className="text-[10px] uppercase tracking-wider text-secondary2 mr-1">Présence :</span>
                    {Object.entries(ATT).map(([k, v]) => (
                      <button key={k} onClick={() => markAttendance(c, k)} data-testid={`att-${k}-${c.id}`}
                        className={`text-[10px] uppercase tracking-wide rounded-full px-2 py-0.5 border ${c.attendance === k || (!c.attendance && k === "non_renseigne") ? v.tone + " bg-black/60" : "text-white/40 border-white/10 hover:border-white/30"}`}>
                        {v.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Confirmation de décision */}
      {confirm && (
        <Modal onClose={() => setConfirm(null)} testid="decision-confirm">
          <h3 className="text-white font-serif text-lg">Confirmer la décision</h3>
          <p className="text-sm text-secondary2 mt-2">
            {confirm.action === "accepter" && <>Accepter <b className="text-white">{confirm.c.name}</b> ? Une place sera réservée.</>}
            {confirm.action === "refuser" && <>Refuser la candidature de <b className="text-white">{confirm.c.name}</b> ? Aucune place ne sera déduite.</>}
            {confirm.action === "liste_attente" && <>Placer <b className="text-white">{confirm.c.name}</b> en liste d'attente ?</>}
            {confirm.action === "annuler" && <>Annuler l'inscription de <b className="text-white">{confirm.c.name}</b> ? La place sera libérée.</>}
          </p>
          <div className="flex justify-end gap-2 mt-4">
            <button onClick={() => setConfirm(null)} className="px-4 py-2 rounded-full border border-white/15 text-white/70 text-xs">Retour</button>
            <button onClick={doDecision} disabled={busy} data-testid="decision-confirm-yes"
              className="px-4 py-2 rounded-full bg-electric text-black text-xs font-semibold disabled:opacity-50 inline-flex items-center gap-2">
              {busy ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} Confirmer
            </button>
          </div>
        </Modal>
      )}

      {/* E-mail de décision pré-rempli (validation avant envoi) */}
      {emailDraft && (
        <Modal onClose={() => setEmailDraft(null)} testid="decision-email" wide>
          <h3 className="text-white font-serif text-lg flex items-center gap-2"><Mail size={16} className="text-electric" /> E-mail au candidat</h3>
          <p className="text-xs text-secondary2 mt-1">Message pré-rempli — relisez et modifiez avant d'envoyer à <b className="text-white">{emailDraft.c.email}</b>.</p>
          <input value={emailDraft.subject} onChange={(e) => setEmailDraft((d) => ({ ...d, subject: e.target.value }))} data-testid="decision-email-subject"
            className="mt-3 w-full bg-[#0e0e0e] border border-white/10 rounded px-3 py-2 text-white text-sm focus:border-electric outline-none" placeholder="Objet" />
          <textarea value={emailDraft.message} onChange={(e) => setEmailDraft((d) => ({ ...d, message: e.target.value }))} rows={9} data-testid="decision-email-body"
            className="mt-2 w-full bg-[#0e0e0e] border border-white/10 rounded px-3 py-2 text-white text-sm focus:border-electric outline-none whitespace-pre-line" />
          <div className="flex justify-end gap-2 mt-3">
            <button onClick={() => setEmailDraft(null)} className="px-4 py-2 rounded-full border border-white/15 text-white/70 text-xs">Annuler</button>
            <button onClick={sendEmail} disabled={emailDraft.sending} data-testid="decision-email-send"
              className="px-4 py-2 rounded-full bg-electric text-black text-xs font-semibold disabled:opacity-50 inline-flex items-center gap-2">
              {emailDraft.sending ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} Envoyer l'e-mail
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
};

const Stat = ({ label, value, tone = "text-white" }) => (
  <div className="rounded-lg border border-white/10 bg-[#0e0e0e] px-3 py-2 text-center">
    <div className={`text-xl font-semibold ${tone}`}>{value}</div>
    <div className="text-[9px] uppercase tracking-[0.15em] text-secondary2">{label}</div>
  </div>
);

const Modal = ({ children, onClose, testid, wide }) => (
  <div className="fixed inset-0 z-[9998] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-testid={testid} onClick={onClose}>
    <div className={`w-full ${wide ? "max-w-2xl" : "max-w-md"} rounded-xl border border-white/10 bg-[#0a0a0a] p-5`} onClick={(e) => e.stopPropagation()}>
      {children}
    </div>
  </div>
);

// Récapitulatif des présences d'une session (utilisé dans la fiche formation)
export const AttendanceSummary = ({ counts }) => (
  <div className="flex flex-wrap items-center gap-3 text-[11px]">
    <span className="inline-flex items-center gap-1 text-green-300"><CheckCircle2 size={12} /> {counts.present} présent(s)</span>
    <span className="inline-flex items-center gap-1 text-red-300"><XCircle size={12} /> {counts.absent} absent(s)</span>
    <span className="inline-flex items-center gap-1 text-amber-300"><AlertCircle size={12} /> {counts.retard} retard(s)</span>
  </div>
);
